import React from 'react';
import type { MessageDescriptor } from 'react-intl';

import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { TYPES_WITH_ACTIVATIONID } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';

import type { FilterStateType, FilterOption, FilterSelections } from '../AdvancedSearch-types.ts';
import {
	SearchConfluenceRangeField,
	type SearchConfluenceDocumentStatus,
	type SearchConfluenceRangeFilter,
	type SearchContainerStatus,
	type SearchConfluenceFilter,
} from '../__types__/useResultSupplierAdvancedAGGSearchQuery.ts';
import {
	QUERY_PARAM_ARRAY_SEPARATOR,
	LastModifiedValues,
	defaultOptionValue,
	FilterIds,
	lastModifiedFilterOptions,
	OptionIds,
	titleFilterOption,
	archivedFilterOption,
	toFilterOption,
	fromFilterOption,
	verifiedOption,
	INCLUDEARCHIVEDSTATUS,
} from '../AdvancedSearch-constants.ts';
import type {
	AdvancedSearchFilterQuery_getSmartFeatures_SmartFeaturesResponse_results_SmartFeaturesUserResultResponse as SmartFeaturesUserResponseType,
	AdvancedSearchFilterQuery_confluenceUsers_PaginatedPersonList_nodes_User as ConfluenceUserType,
	AdvancedSearchFilterQuery_confluenceUsers_PaginatedPersonList_nodes_KnownUser as KnownUserType,
	AdvancedSearchFilterQuery_confluenceUsers_PaginatedPersonList_nodes_UnknownUser as UnknownUserType,
} from '../gql/__types__/AdvancedSearchFilterQuery.ts';

type UserType = ConfluenceUserType | KnownUserType | UnknownUserType;

// creates truncated iso strings by converting mm-dd-yyyy strings to ISO format
// considering the user's locale-based definition of start OR end of day
export const getUTCStartEnd = (date: string, isStartOfDay: boolean): string => {
	const currentDate = new Date(date);
	return isStartOfDay
		? new Date(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate())
				.toISOString()
				.split('Z')[0]
		: new Date(
				currentDate.getUTCFullYear(),
				currentDate.getUTCMonth(),
				currentDate.getUTCDate(),
				23,
				59,
				59,
				999,
			)
				.toISOString()
				.split('Z')[0];
};

// generates truncated iso strings for last modified filter defaults (today, yesterday, etc)
// considering the user's locale-based definition of start OR end of day
const getUTCDateRange = (fromOffset: number, toOffset: number): { from: string; to: string } => {
	const currentDate = new Date();
	return {
		from: new Date(
			new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime() -
				fromOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
		to: new Date(
			new Date(
				currentDate.getFullYear(),
				currentDate.getMonth(),
				currentDate.getDate(),
				23,
				59,
				59,
				999,
			).getTime() -
				toOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
	};
};

export const createAggFilters = (
	filters: FilterStateType,
	activationId: string | null,
): SearchConfluenceFilter => {
	const confluenceFilters: SearchConfluenceFilter = {};

	const selectedSpaces = filters[FilterIds.SPACES].filter(
		(spaceOption) => spaceOption.value !== 'default' && spaceOption.isSelected,
	);
	if (selectedSpaces.length > 0) {
		confluenceFilters.spacesFilter = selectedSpaces.map((spaceOption) => spaceOption.value);
	}

	const selectedContributors = filters[FilterIds.CONTRIBUTORS].filter(
		(contributorOption) => contributorOption.value !== 'default' && contributorOption.isSelected,
	);
	if (selectedContributors.length > 0) {
		confluenceFilters.contributorsFilter = selectedContributors.map(
			(contributorOption) => contributorOption.value,
		);
	}

	const selectedLabels = filters[FilterIds.LABELS].filter((labelOption) => labelOption.isSelected);
	if (selectedLabels.length > 0) {
		confluenceFilters.labelsFilter = selectedLabels.map((labelOption) => labelOption.value);
	}
	const selectedMoreFilterOptions = filters[FilterIds.MORE].filter(
		(moreFilterOption) => moreFilterOption.isSelected,
	);

	if (selectedMoreFilterOptions.length > 0) {
		const selectedValues = selectedMoreFilterOptions.map(
			(moreFilterOption) => moreFilterOption.value,
		);
		if (selectedValues.includes(OptionIds.ARCHIVED)) {
			// Casting SearchConfluenceDocumentStatus & SearchContainerStatus as enum[] cannot match string[]
			confluenceFilters.contentStatuses = INCLUDEARCHIVEDSTATUS as SearchConfluenceDocumentStatus[];
			confluenceFilters.containerStatus = INCLUDEARCHIVEDSTATUS as SearchContainerStatus[];
		}
		if (selectedValues.includes(OptionIds.TITLE)) {
			confluenceFilters.titleMatchOnly = true;
		}
	}

	const selectedAncestorFilterOptions = filters[FilterIds.ANCESTOR].filter(
		(moreFilterOption) => moreFilterOption.isSelected,
	);

	if (selectedAncestorFilterOptions.length > 0 && selectedAncestorFilterOptions[0].value) {
		confluenceFilters.ancestorIdsFilter = selectedAncestorFilterOptions.map(({ type, value }) => {
			if (type && TYPES_WITH_ACTIVATIONID.includes(type)) {
				return `activation/${activationId}/${value}`;
			}

			return value;
		});
	}

	const selectedDateFilters = filters[FilterIds.DATE].lastModified.filter(
		(dateOption) => dateOption.value !== 'default' && dateOption.isSelected,
	);
	if (selectedDateFilters.length > 0) {
		// only one date filter can be applied at a time
		const selectedDateFilter = selectedDateFilters[0];
		const selectedDateFilterType = selectedDateFilter.value;

		let from: SearchConfluenceRangeFilter['gt'];
		let to: SearchConfluenceRangeFilter['lt'];
		if (selectedDateFilterType !== 'custom') {
			const transformedDateRange = getLastModifiedParam(selectedDateFilterType);
			const dateRange = confDateValueOptions[transformedDateRange]?.dateRange;
			from = dateRange?.from || '';
			to = dateRange?.to || '';
		} else {
			from = filters[FilterIds.DATE].from.value
				? getUTCStartEnd(filters[FilterIds.DATE].from.value, true)
				: null;
			to = filters[FilterIds.DATE].to.value
				? getUTCStartEnd(filters[FilterIds.DATE].to.value, false)
				: null;
		}

		if (!(selectedDateFilterType === 'custom' && !from && !to)) {
			const lastModifiedRange = {
				field: SearchConfluenceRangeField.LASTMODIFIED,
				gt: from,
				lt: to,
			};
			confluenceFilters.range = [lastModifiedRange];
		}
	}

	const isVerified = filters[FilterIds.VERIFIED].some(
		(option: FilterOption) => option.value === FilterIds.VERIFIED && option.isSelected,
	);
	if (isVerified) {
		confluenceFilters.isVerified = isVerified;
	}

	return confluenceFilters;
};

export const confDateValueOptions: {
	[key: string]: {
		dateRange?: {
			from: string;
			to: string;
		};
	};
} = {
	[LastModifiedValues.ANY_TIME]: {},
	[LastModifiedValues.TODAY]: {
		dateRange: getUTCDateRange(0, 0),
	},
	[LastModifiedValues.YESTERDAY]: {
		dateRange: getUTCDateRange(1, 1),
	},
	[LastModifiedValues.PAST_SEVEN_DAYS]: {
		dateRange: getUTCDateRange(7, 0),
	},
	[LastModifiedValues.PAST_THIRTY_DAYS]: {
		dateRange: getUTCDateRange(30, 0),
	},
	[LastModifiedValues.PAST_YEAR]: {
		dateRange: getUTCDateRange(365, 0),
	},
	[LastModifiedValues.CUSTOM]: {},
};

export const getLastModifiedParam = (lastModifiedValue: string): string => {
	//translates previously-used values ("lastDay", "lastWeek", "lastMonth", "lastYear")
	switch (lastModifiedValue) {
		case LastModifiedValues.LAST_DAY:
			return LastModifiedValues.TODAY;
		case LastModifiedValues.LAST_WEEK:
			return LastModifiedValues.PAST_SEVEN_DAYS;
		case LastModifiedValues.LAST_MONTH:
			return LastModifiedValues.PAST_THIRTY_DAYS;
		case LastModifiedValues.LAST_YEAR:
			return LastModifiedValues.PAST_YEAR;
		default:
			return lastModifiedValue;
	}
};

export const getOptionsToHydrate = (
	queryParams: any,
	filters: any,
): {
	contributors: any;
	spaces: any;
	contributorsToHydrate: any;
	spacesToHydrate: any;
	ancestorId: any;
	selectedType: any;
	lastModified: string | undefined;
} => ({
	contributors: queryParams?.contributors
		? queryParams.contributors.split(QUERY_PARAM_ARRAY_SEPARATOR)
		: [],
	spaces: queryParams?.spaces ? queryParams.spaces.split(QUERY_PARAM_ARRAY_SEPARATOR) : [],
	contributorsToHydrate: (queryParams?.contributors
		? queryParams.contributors.split(QUERY_PARAM_ARRAY_SEPARATOR)
		: []
	).filter(
		(key) =>
			!filters[FilterIds.CONTRIBUTORS].some((option) => option.value === key) ||
			filters[FilterIds.CONTRIBUTORS].some(
				(option) => option.value === key && option.avatarUrl === undefined,
			),
	),
	spacesToHydrate: (queryParams?.spaces
		? queryParams.spaces.split(QUERY_PARAM_ARRAY_SEPARATOR)
		: []
	).filter(
		(key) =>
			!filters[FilterIds.SPACES].some((option) => option.value === key) ||
			filters[FilterIds.SPACES].some(
				(option) => option.value === key && option.avatarUrl === undefined,
			),
	),
	ancestorId: queryParams?.ancestor || '',
	selectedType: queryParams?.type,
	lastModified: queryParams?.lastModified
		? getLastModifiedParam(queryParams.lastModified)
		: undefined,
});

// This function generates the initial option array for a given filterId
// using the default options (with appropriate isSelected value)
// & any non-default option selections transformed into FilterOption objects
const transformQueryParams = (defaultOptions: FilterOption[], params: string[] | string) => {
	const queryParams = [...params];

	const selectedDefaults = defaultOptions.map((option) => ({
		...option,
		isSelected: queryParams.includes(option.value),
	}));

	const optionsFromParams = queryParams
		.filter((param) => param && !selectedDefaults.some(({ value }) => value === param))
		.map((paramValue) => ({
			isSelected: true,
			value: paramValue,
			label: paramValue,
		}));

	return [...selectedDefaults, ...optionsFromParams];
};

// This function initializes the query param object as the entire initial filter state object
// inclusive of static/default options & non-default option selections provided in the query params
export const initializeFilters = (
	queryParams: any,
): {
	date: {
		lastModified: (
			| {
					isSelected: boolean;
					value: string;
					label: string | MessageDescriptor;
					avatarUrl?: string;
					optionId?: string;
					isSmartDefault?: boolean;
					type?: string;
					iconType?: string;
					icon?: React.ReactNode;
			  }
			| {
					isSelected: boolean;
					value: string;
					label: string;
			  }
		)[];
		to: {
			value: any;
			isSelected: boolean;
			label: string;
			optionId: OptionIds;
		};
		from: {
			value: any;
			isSelected: boolean;
			label: string;
			optionId: OptionIds;
		};
	};
	spaces: (
		| {
				isSelected: boolean;
				value: string;
				label: string | MessageDescriptor;
				avatarUrl?: string;
				optionId?: string;
				isSmartDefault?: boolean;
				type?: string;
				iconType?: string;
				icon?: React.ReactNode;
		  }
		| {
				isSelected: boolean;
				value: string;
				label: string;
		  }
	)[];
	contributors: (
		| {
				isSelected: boolean;
				value: string;
				label: string | MessageDescriptor;
				avatarUrl?: string;
				optionId?: string;
				isSmartDefault?: boolean;
				type?: string;
				iconType?: string;
				icon?: React.ReactNode;
		  }
		| {
				isSelected: boolean;
				value: string;
				label: string;
		  }
	)[];
	labels: (
		| {
				isSelected: boolean;
				value: string;
				label: string | MessageDescriptor;
				avatarUrl?: string;
				optionId?: string;
				isSmartDefault?: boolean;
				type?: string;
				iconType?: string;
				icon?: React.ReactNode;
		  }
		| {
				isSelected: boolean;
				value: string;
				label: string;
		  }
	)[];
	type: (
		| {
				isSelected: boolean;
				value: string;
				label: string | MessageDescriptor;
				avatarUrl?: string;
				optionId?: string;
				isSmartDefault?: boolean;
				type?: string;
				iconType?: string;
				icon?: React.ReactNode;
		  }
		| {
				isSelected: boolean;
				value: string;
				label: string;
		  }
	)[];
	ancestor: {
		label: string;
		value: any;
		isSelected: boolean;
	}[];
	verified: {
		isSelected: boolean;
		value: string;
		label: {
			id: string;
			defaultMessage: string;
			description: string;
		};
	}[];
	moreFilters: {
		isSelected: boolean;
		label: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		value: string;
	}[];
} => {
	const {
		lastModified = '',
		from = '',
		to = '',
		spaces = '',
		contributors = '',
		labels = '',
		type = '',
		verified = '',
		title,
		archived,
		ancestor,
	} = queryParams;
	return {
		[FilterIds.DATE]: {
			[OptionIds.LAST_MODIFIED]: transformQueryParams(lastModifiedFilterOptions, [
				getLastModifiedParam(lastModified.split(QUERY_PARAM_ARRAY_SEPARATOR)[0]),
			]),
			[OptionIds.TO]: { ...toFilterOption, value: to || '', isSelected: !!to },
			[OptionIds.FROM]: {
				...fromFilterOption,
				value: from || '',
				isSelected: !!from,
			},
		},
		[FilterIds.SPACES]: transformQueryParams([], spaces.split(QUERY_PARAM_ARRAY_SEPARATOR)),
		[FilterIds.CONTRIBUTORS]: transformQueryParams(
			[],
			contributors.split(QUERY_PARAM_ARRAY_SEPARATOR),
		),
		[FilterIds.LABELS]: transformQueryParams([], labels.split(QUERY_PARAM_ARRAY_SEPARATOR)),
		[FilterIds.TYPE]: transformQueryParams([], type.split(QUERY_PARAM_ARRAY_SEPARATOR)),
		[FilterIds.ANCESTOR]: ancestor
			? [
					{
						label: '',
						value: ancestor || '',
						isSelected: !!ancestor,
					},
				]
			: [],
		[FilterIds.VERIFIED]: [
			{
				...verifiedOption,
				isSelected: !!verified,
			},
		],
		[FilterIds.MORE]: [
			{
				...titleFilterOption,
				isSelected: !!title,
			},
			{
				...archivedFilterOption,
				isSelected: !!archived,
			},
		],
	};
};

export const generateUpdatedQueryParams = (filters: FilterStateType): {} => {
	const updatedQueryParams = {};
	Object.entries(filters).forEach(([filterId, filterValues]) => {
		if (filterId === FilterIds.MORE) {
			const selectedValues = filterValues
				.filter((filter) => filter && filter.isSelected && filter.value)
				.map((filter) => filter.value.toString());
			updatedQueryParams[OptionIds.ARCHIVED] =
				selectedValues.includes(OptionIds.ARCHIVED) || undefined;
			updatedQueryParams[OptionIds.TITLE] = selectedValues.includes(OptionIds.TITLE) || undefined;
		} else if (filterId === FilterIds.DATE) {
			Object.keys(filterValues).forEach((param) => {
				if (param === OptionIds.LAST_MODIFIED) {
					const selectedOption = filterValues[param].find((filter) => filter.isSelected);
					updatedQueryParams[param] = selectedOption ? selectedOption.value.toString() : undefined;
				} else if (param === OptionIds.TO || param === OptionIds.FROM) {
					const isCustom =
						updatedQueryParams[OptionIds.LAST_MODIFIED] === LastModifiedValues.CUSTOM;
					updatedQueryParams[param] =
						!!filterValues[param]?.isSelected && isCustom
							? filterValues[param].value.toString()
							: undefined;
				}
			});
		} else if (filterId === FilterIds.VERIFIED) {
			const selectedValues = filterValues
				.filter((filter) => filter && filter.isSelected && filter.value)
				.map((filter) => filter.value.toString());

			updatedQueryParams[OptionIds.VERIFIED] = selectedValues.includes('verified') || undefined;
		} else {
			const selectedValues = filterValues
				.filter((filter) => filter && filter.isSelected && filter.value)
				.map((filter) => filter.value.toString());
			updatedQueryParams[filterId] = selectedValues.length
				? selectedValues.join(QUERY_PARAM_ARRAY_SEPARATOR)
				: undefined;
		}
	});
	return { ...updatedQueryParams };
};

export const getSelectedOptions = (filterId: string, filters: FilterStateType): FilterOption[] => {
	const options = filters[filterId];
	if (!options) return [];

	if (filterId === FilterIds.DATE) {
		const dateFilterSelections: any[] = [];
		if (!!filters[filterId][OptionIds.LAST_MODIFIED].some((option) => option.isSelected)) {
			dateFilterSelections.push({
				...filters[filterId][OptionIds.LAST_MODIFIED].find((option) => option.isSelected),
			});
		}
		if (!!filters[filterId][OptionIds.FROM].isSelected) {
			dateFilterSelections.push(filters[filterId][OptionIds.FROM]);
		}
		if (!!filters[filterId][OptionIds.TO].isSelected) {
			dateFilterSelections.push(filters[filterId][OptionIds.TO]);
		}
		return dateFilterSelections;
	}
	if (!Array.isArray(options)) {
		return !!options?.isSelected ? [options] : [];
	}
	return (
		options
			.filter((option) => option.isSelected && option.value !== defaultOptionValue)
			.map((option) => ({ ...option, filterId })) || []
	);
};

export const getOptionSelections = (filters: any): FilterSelections[] => {
	const allSelectedOptions: FilterSelections[][] = [];
	Object.keys(filters).forEach((filterId) => {
		const optionSelections: FilterSelections[] = getSelectedOptions(filterId, filters).map(
			(option) => ({
				value: option.value,
				isSmartDefault: !!option.isSmartDefault,
				filterId,
			}),
		);
		allSelectedOptions.push(optionSelections);
	});
	return allSelectedOptions.flat();
};

//returns a list of filter ids that have been selected
//with handling for the 'type' filter per v1 analytics
export const transformFiltersForAnalytics = (filterSelections: any): unknown[] => {
	const transformedFilters = filterSelections.map((option) =>
		option.filterId === FilterIds.TYPE ? `type.${option.value}` : option.filterId,
	);
	return Array.from(new Set(transformedFilters));
};

export const areFiltersSelected = (filters: FilterStateType, skipMoreFilters: boolean): boolean => {
	return Object.keys(filters).some((filterId) => {
		switch (filterId) {
			case FilterIds.DATE:
				return (
					filters[filterId][OptionIds.LAST_MODIFIED].some(
						(lastModifiedOption) => lastModifiedOption.isSelected,
					) ||
					filters[filterId][OptionIds.TO].isSelected ||
					filters[filterId][OptionIds.FROM].isSelected
				);
			case FilterIds.MORE:
				//we don't count "more" filter option selections as user input when using this function
				//for agg query skip - these selections require additional input to have an effect
				return skipMoreFilters
					? false
					: filters[filterId].some((filterOption) => !!filterOption?.isSelected);
			default:
				return filters[filterId].some((filterOption) => !!filterOption?.isSelected);
		}
	});
};

const transformAncestorSelection = (option: FilterOption) => {
	return {
		value: option.value,
		label: option.label,
		isSelected: option.isSelected,
		type: option.type,
		icon: <ContentIcon type={option.type || 'page'} />,
	};
};

export const mapDataToOptions = ({
	data,
	spaces,
	contributors,
	contributorsToHydrate,
	spacesToHydrate,
	selectedType,
	defaultTypesFilterOptions,
	isWhiteboardsEnabledAS,
	isDatabasesEnabledAS,
	isSlidesEnabledAS,
	userId,
}: {
	data: any;
	spaces: any;
	contributors: any;
	contributorsToHydrate: any;
	spacesToHydrate: any;
	selectedType: any;
	defaultTypesFilterOptions: any;
	isWhiteboardsEnabledAS: any;
	isDatabasesEnabledAS: any;
	isSlidesEnabledAS: any;
	userId: any;
}): {
	ancestorOptions: {
		value: string;
		label: string | MessageDescriptor;
		isSelected: boolean;
		type: string | undefined;
		icon: React.JSX.Element;
	}[];
	hydratedTypes: any;
	spaceOptions: any[];
	contributorOptions: any[];
} => {
	const recommendations = (data?.getSmartFeatures?.results?.[0] as SmartFeaturesUserResponseType)
		?.result?.[0]?.features;

	const hydratedUsers = (data?.confluenceUsers?.nodes || []).map((user) => ({
		value: (user as UserType).accountId || '',
		label: user?.displayName || '',
		avatarUrl: user?.profilePicture?.path || '',
		isSelected: true,
	}));

	const userRecommendations = (recommendations?.recommendedPeople || [])
		.map((user) => ({
			value: user?.user?.id || '',
			label: user?.user?.displayName || '',
			avatarUrl: user?.user?.photos?.[0]?.value || '',
			isSelected: contributors.includes(user?.user?.id || ''),
			isSmartDefault: true,
		}))
		.filter((option) => !contributorsToHydrate.includes(option.value) || option.value === userId);

	const hydratedSpaces = (data?.spaces?.nodes || []).map((space) => ({
		value: space?.key || '',
		label: space?.name || '',
		avatarUrl: space?.icon?.path || '',
		isSelected: true,
	}));

	const spaceRecommendations = (recommendations?.recommendedSpaces || [])
		.map((space) => ({
			value: space?.space?.key || '',
			label: space?.space?.name || '',
			avatarUrl: space?.space?.icon?.path || '',
			isSelected: spaces.includes(space?.space?.key || ''),
			isSmartDefault: true,
		}))
		.filter((option) => !spacesToHydrate.includes(option.value));

	const hydratedTypes = (
		data?.contentTypes?.cqlContentTypes.filter(
			(contentType) =>
				defaultTypesFilterOptions.find((defaultType) => {
					const { type } = contentType;
					if (type?.toLowerCase() === 'whiteboard' && !isWhiteboardsEnabledAS) {
						// When FF is off return true to ensure that we do not include whiteboard content type in hydrated list
						return true;
					}
					if (type?.toLowerCase() === 'database' && !isDatabasesEnabledAS) {
						// When FF is off return true to ensure that we do not include database content type in hydrated list
						return true;
					}
					if (type?.toLowerCase() === 'slide' && !isSlidesEnabledAS) {
						// When FF is off return true to ensure that we do not include slide content type in hydrated list
						return true;
					}
					if (type?.toLowerCase() === 'folder') {
						// When FF is off return true to ensure that we do not include folder content type in hydrated list
						return true;
					}
					return defaultType.value === type;
				}) === undefined,
		) || []
	).map((type) => ({
		value: type?.type || '',
		label: type?.label || '',
		isSelected: type?.type === selectedType,
	}));

	const hydratedAncestor = data?.ancestorData?.id
		? {
				value: data?.ancestorData?.id || '',
				label: data?.ancestorData?.title || '',
				type: data?.ancestorData?.type || '',
				isSelected: true,
			}
		: undefined;

	const ancestorRecommendations = data?.activity?.myActivity?.all?.edges
		? data.activity.myActivity.all.edges
				.filter((edge) => edge?.node?.object?.data)
				.map((edge) => {
					const nodeData = edge?.node?.object?.data as
						| { __typename: string; id?: string; title?: string | null; type?: string | null }
						| null
						| undefined;
					const normalizedType =
						nodeData?.type?.toLowerCase() === 'blog_post' ? 'blogpost' : nodeData?.type || '';
					return {
						value: nodeData?.id || '',
						label: nodeData?.title || '',
						isSelected: false,
						type: normalizedType,
					};
				})
		: [];

	const ancestorOptions = [
		...(hydratedAncestor ? [hydratedAncestor] : []),
		...ancestorRecommendations,
	]
		.filter((ancestorOption) => ancestorOption?.value && ancestorOption.value !== '')
		.map((ancestor) => transformAncestorSelection(ancestor));

	const currentUserOption =
		data?.currentUser?.confluence?.accountId &&
		!contributors.includes(data.currentUser.confluence.accountId)
			? {
					value: data.currentUser.confluence.accountId,
					label: data?.currentUser?.displayName || '',
					avatarUrl: data?.currentUser?.photos.find(({ isPrimary }) => isPrimary)?.value || '',
					isSelected: false,
					isSmartDefault: true,
				}
			: undefined;

	return {
		ancestorOptions,
		hydratedTypes,
		spaceOptions:
			hydratedSpaces.length < 5
				? [...hydratedSpaces, ...spaceRecommendations.slice(0, 5 - hydratedSpaces.length)]
				: [...hydratedSpaces],
		contributorOptions:
			hydratedUsers.length < 5
				? [
						...hydratedUsers,
						...(currentUserOption ? [currentUserOption] : []),
						...userRecommendations.slice(0, 5 - hydratedUsers.length),
					]
				: [...hydratedUsers, ...(currentUserOption ? [currentUserOption] : [])],
	};
};

export const areFiltersConsistent = (
	filters: FilterStateType,
	keysFromQueryParams: string[],
): boolean => {
	const quickSearchFilters = [
		...filters[FilterIds.SPACES].filter(
			(option) => option.isSelected && option.value !== defaultOptionValue,
		),
		...filters[FilterIds.CONTRIBUTORS].filter(
			(option) => option.isSelected && option.value !== defaultOptionValue,
		),
		...filters[FilterIds.DATE][OptionIds.LAST_MODIFIED].filter(
			(option) => option.isSelected && option.value !== defaultOptionValue,
		),
	].map(({ value }) => value);

	return (
		[...quickSearchFilters, ...keysFromQueryParams].length === 0 ||
		(keysFromQueryParams.every((key) => quickSearchFilters.includes(key)) &&
			quickSearchFilters.some((selectedValue) => keysFromQueryParams.includes(selectedValue)))
	);
};

export const mapFiltersToAggregator = (
	filters: FilterStateType | undefined,
): {
	[key: string]: any;
	'@type': string;
}[] => {
	if (!filters) {
		return [];
	}
	const selectedFilters: Array<{ '@type': string; [key: string]: any }> = [];
	if (filters[FilterIds.SPACES]) {
		const spaceKeys = filters[FilterIds.SPACES]
			.filter((option) => !!option.isSelected)
			.map((option) => option.value);
		if (spaceKeys.length > 0) {
			selectedFilters.push({
				'@type': 'spaces',
				spaceKeys,
			});
		}
	}
	if (filters[FilterIds.CONTRIBUTORS]) {
		const accountIds = filters[FilterIds.CONTRIBUTORS]
			.filter((option) => !!option.isSelected)
			.map((option) => option.value);
		if (accountIds.length > 0) {
			selectedFilters.push({
				'@type': 'contributors',
				accountIds,
			});
		}
	}

	const selectedDateFilters = filters[FilterIds.DATE].lastModified.filter(
		(dateOption) => dateOption.value !== 'default' && dateOption.isSelected,
	);
	if (selectedDateFilters.length > 0) {
		const selectedDateFilter = selectedDateFilters[0];
		const selectedDateFilterType = selectedDateFilter.value;

		let from;
		let to;
		if (selectedDateFilterType !== 'custom') {
			const transformedDateRange = getLastModifiedParam(selectedDateFilterType);
			const dateRange = confDateValueOptions[transformedDateRange]?.dateRange;
			from = dateRange?.from || '';
			to = dateRange?.to || '';
		} else {
			from = filters[FilterIds.DATE].from.value
				? getUTCStartEnd(filters[FilterIds.DATE].from.value, true)
				: null;
			to = filters[FilterIds.DATE].to.value
				? getUTCStartEnd(filters[FilterIds.DATE].to.value, false)
				: null;
		}
		if (!(selectedDateFilterType === 'custom' && !from && !to)) {
			selectedFilters.push({
				'@type': 'lastModified',
				from,
				to,
			});
		}
	}

	if (filters[FilterIds.LABELS]) {
		const labels = filters[FilterIds.LABELS]
			.filter((option) => !!option.isSelected)
			.map((option) => option.value);
		if (labels.length > 0) {
			selectedFilters.push({
				'@type': 'labels',
				labels,
			});
		}
	}
	if (filters[FilterIds.MORE]) {
		filters[FilterIds.MORE]
			.filter((moreFilterOption) => moreFilterOption.isSelected)
			.forEach((moreFilterOption) => {
				if (moreFilterOption.value === OptionIds.TITLE) {
					selectedFilters.push({
						'@type': 'titleMatchOnly',
						titleMatchOnly: true,
					});
				}
				if (moreFilterOption.value === OptionIds.ARCHIVED) {
					selectedFilters.push({
						'@type': 'statuses',
						statuses: INCLUDEARCHIVEDSTATUS,
					});
					selectedFilters.push({
						'@type': 'containerStatuses',
						containerStatuses: INCLUDEARCHIVEDSTATUS,
					});
				}
			});
	}
	if (filters[FilterIds.ANCESTOR]) {
		const ancestorIds = filters[FilterIds.ANCESTOR]
			.filter((option) => !!option.isSelected)
			.map((option) => option.value);
		if (ancestorIds.length > 0) {
			selectedFilters.push({
				'@type': 'ancestorIds',
				ancestorIds,
			});
		}
	}
	return selectedFilters;
};
